import React, { useState } from "react";
import { useGetEmployees } from "../../hooks/useEmployeeApi";
import { useCreateBulkTasks, useCreateTasks } from "../../hooks/useTaskApi";
import { TaskEntryForm } from "./TaskEntryForm";
import { toast } from "react-toastify";
import { useAuth } from "../../hooks/AuthContext";
import { CreateProjectDialog } from "../Models/CreateProjectDialog";
import { Plus } from "lucide-react";

export const HrTaskCreation = () => {
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [assignToMultiple, setAssignToMultiple] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState("");

  const { data: employeesData, isLoading: isLoadingEmployees } = useGetEmployees();
  const { mutate: createTasks, isLoading: isCreating } = useCreateTasks();
  const { mutate: createBulkTasks } = useCreateBulkTasks();
  const { user } = useAuth();

  const employeeOptions =
    employeesData?.employees.map((emp) => ({
      value: emp._id,
      label: `${emp.name} (${emp.employeeId})`,
    })) || [];

  const handleFormSubmit = (formData) => {
    let tasks = formData.tasks.map((task) => ({
      ...task,
      project_id: selectedProjectId,
      createdBy: user?.employeeProfile?._id || "68c15ce1855dbd716909c257",
    }));

    if (!assignToMultiple) {
      if (!selectedEmployeeId) {
        alert("Please select an employee first.");
        return;
      }
      tasks = tasks.map((task) => ({
        ...task,
        assignedTo: selectedEmployeeId,
      }));
    }

    const missingEmployee = tasks.some((task) => !task.assignedTo);
    if (missingEmployee) {
      alert("Please assign an employee to each task.");
      return;
    }

    if (tasks.length === 1) {
      const payload = tasks[0];
      createTasks(payload, {
        onSuccess: () => toast.success("Task submitted successfully!"),
        onError: (error) =>
          alert(`Error: ${error.response?.data?.message || error.message}`),
      });
    } else {
      const payload = {
        project_id: selectedProjectId,
        tasks,
      };
      createBulkTasks(payload, {
        onSuccess: () => toast.success("Tasks submitted successfully!"),
        onError: (error) =>
          alert(`Error: ${error.response?.data?.message || error.message}`),
      });
    }
  };

  return (
    <div >
      <h1 className="text-xl font-bold mb-2 px-3 ">Create Tasks for Employees</h1>
      <p className="text-gray-600 mb-3 px-3">
        Choose how you want to assign tasks to employees.
      </p>

      {/* Assignment Mode Selection */}
      <div className="mb-3 px-3">
        <div className="flex items-center space-x-4">
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="assignmentMode"
              checked={!assignToMultiple}
              onChange={() => setAssignToMultiple(false)}
              className="form-radio h-5 w-5 text-[#2C4E86]"
            />
            <span className="ml-2 text-gray-700">
              Assign all tasks to one employee
            </span>
          </label>
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="assignmentMode"
              checked={assignToMultiple}
              onChange={() => setAssignToMultiple(true)}
              className="form-radio h-5 w-5 text-[#2C4E86]"
            />
            <span className="ml-2 text-gray-700">
              Assign each task to different employees
            </span>
          </label>
        </div>
      </div>

      <div className="w-full mb-5 px-3 flex justify-between items-center">
        {/* Employee Selection */}
        <div>
          {!assignToMultiple && (
            <div className=" max-w-sm">
              <label className="block text-gray-700 mb-2">Select Employee</label>
              <select
                value={selectedEmployeeId}
                onChange={(e) => setSelectedEmployeeId(e.target.value)}
                className="w-full p-2 border rounded"
                disabled={isLoadingEmployees}
              >
                <option value="">
                  {isLoadingEmployees ? "Loading..." : "Select an employee"}
                </option>
                {employeeOptions.map((opt) => (
                  <option key={opt?.value} value={opt?.value}>
                    {opt?.label}
                  </option>
                ))}
              </select>
            </div>
          )}

        </div>
        {/* ✅ Create Project Button */}
        <div>
         
            <button
              onClick={() => setIsDialogOpen(true)}
              className="px-2 py-2  flex gap-1 bg-[#2C4E86] text-white rounded-md hover:bg-[#1f3764]"
            >
              <Plus /> Create Project
            </button>
         

          {/* ✅ Imported CreateProjectDialog */}
          <CreateProjectDialog
            open={isDialogOpen}
            onClose={() => setIsDialogOpen(false)}
          />
        </div>

      </div>
      {/* Task Entry Form */}
      {(assignToMultiple || selectedEmployeeId) && (
        <TaskEntryForm
          onSubmit={handleFormSubmit}
          isLoading={isCreating}
          employeeOptions={employeeOptions}
          showEmployeeField={assignToMultiple}
          onProjectSelect={setSelectedProjectId}
        />
      )}
    </div>
  );
};
